import * as React from 'react';
import type { AgentSendAttachment } from '@volley/crucible-ipc-types/ipc-channels';
import { cn } from '../lib/cn';

/**
 * Composer attachment thumbnail — replaces the "IMAGE 295 KB" text
 * pill (#193 MVP) with the actual image, since the pixels are the
 * information and the byte size isn't (it's still enforced by the
 * caps, just not displayed).
 *
 * Hover/focus reveals a larger preview at the image's EXACT aspect
 * ratio (fit, never cropped/stretched). The #193 redaction note is
 * NOT here — it's global/static, so it lives once per row in
 * `<AttachmentRedactionInfo>` rather than on every thumbnail. The
 * per-image #212 resize label does stay here (it varies per image).
 */

export interface AttachmentThumbnailProps {
  attachment: AgentSendAttachment;
  onRemove: () => void;
}

export function AttachmentThumbnail(props: AttachmentThumbnailProps): React.JSX.Element {
  const { attachment, onRemove } = props;
  const dataUrl = `data:${attachment.mimeType};base64,${attachment.base64}`;
  // Issue #212 — keep surfacing the downscale so the user knows the
  // sent pixels differ from the source; it lives in the popover now.
  const resizeLabel = attachment.resizedFrom
    ? `Resized ${attachment.resizedFrom.width}×${attachment.resizedFrom.height} → ${attachment.resizedFrom.finalWidth}×${attachment.resizedFrom.finalHeight}`
    : null;

  return (
    <div className="group relative focus-within:z-20 hover:z-20" data-testid="chat-attachment-chip">
      <img
        src={dataUrl}
        alt="Attached image"
        className={cn(
          'h-14 w-auto min-w-[40px] max-w-[120px] rounded-[var(--radius-md)]',
          'border border-white/[0.12] object-contain transition-[border-color]',
          'group-hover:border-white/[0.28]',
        )}
        data-testid="chat-attachment-thumb"
      />

      {/* Remove — dedicated control instead of whole-chip click so a
          hover-inspect can't accidentally destroy the attachment.
          Always visible (not hover-gated): it's the only way to drop
          an attachment, so it must be reachable on touch / hoverless
          pointers too (cursor[bot] #1865). */}
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove attachment"
        title="Remove from this message"
        data-testid="chat-attachment-remove"
        className={cn(
          'absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full',
          'border border-white/[0.2] bg-[#18151f] text-[10px] leading-none text-white/70',
          'transition-colors hover:bg-red-500/80 hover:text-white',
          'focus-visible:bg-red-500/80 focus-visible:text-white',
        )}
      >
        ×
      </button>

      {/* Hover/focus popover: the image at its EXACT aspect ratio in a
          bigger frame (w/h auto under max constraints = no crop, no
          stretch), with the resize label beneath.

          The visual gap above the thumbnail is `pb-2` PADDING on this
          positioning wrapper, NOT margin — so it's part of the wrapper's
          (group-descendant, pointer-events-auto) box. Moving the cursor
          from the thumbnail up to the preview therefore never crosses
          dead space that drops `group-hover` and hides the panel
          (cursor[bot] #1865). */}
      <div
        className={cn(
          'absolute bottom-full left-0 hidden pb-2',
          'group-focus-within:block group-hover:block',
        )}
      >
        <div
          role="tooltip"
          data-testid="chat-attachment-preview-pop"
          className={cn(
            'flex w-max flex-col gap-1.5',
            'rounded-[var(--radius-lg)] border border-white/[0.14] bg-[#18151f] p-2 shadow-xl',
          )}
        >
          <img
            src={dataUrl}
            alt=""
            className="h-auto max-h-[260px] w-auto max-w-[340px] rounded-[var(--radius-md)]"
          />
          {resizeLabel && (
            <span
              className="max-w-[340px] text-[10px] text-white/50"
              data-testid="chat-attachment-resize-label"
            >
              {resizeLabel}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
